import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError, eur, deDate } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Download, FileBarChart, ArrowRight } from "lucide-react";

const iso = (d) => d.toISOString().slice(0, 10);

function presetRange(key) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  if (key === "heute") return [iso(now), iso(now)];
  if (key === "woche") { const mo = new Date(now); mo.setDate(now.getDate() - ((now.getDay() + 6) % 7)); return [iso(mo), iso(now)]; }
  if (key === "monat") return [iso(new Date(y, m, 1)), iso(now)];
  if (key === "quartal") { const q = Math.floor(m / 3) * 3; return [iso(new Date(y, q, 1)), iso(now)]; }
  if (key === "jahr") return [`${y}-01-01`, `${y}-12-31`];
  return null;
}

const PRESETS = [["heute", "Heute"], ["woche", "Woche"], ["monat", "Monat"], ["quartal", "Quartal"], ["jahr", "Jahr"], ["frei", "Frei"]];

function Kpi({ label, value, sub, accent, testid }) {
  return (
    <div className={`rounded-xl p-4 border shadow-sm ${accent ? "bg-emerald-50/80 border-emerald-200" : "bg-white border-zinc-200"}`} data-testid={testid}>
      <div className={`text-[10px] font-mono uppercase tracking-wider ${accent ? "text-emerald-700" : "text-zinc-400"}`}>{label}</div>
      <div className={`font-display text-xl font-semibold tracking-tight mt-1 ${accent ? "text-emerald-600" : ""}`}>{value}</div>
      {sub && <div className={`text-xs mt-1 ${accent ? "text-emerald-700/70" : "text-zinc-400"}`}>{sub}</div>}
    </div>
  );
}

const Row = ({ label, value, strong }) => (
  <div className="flex justify-between flex-wrap gap-x-6 py-1.5 text-sm">
    <span className={`min-w-0 ${strong ? "text-zinc-900 font-medium" : "text-zinc-500"}`}>{label}</span>
    <span className={`font-mono text-right ml-auto ${strong ? "font-semibold text-zinc-900" : "text-zinc-800"}`}>{value}</span>
  </div>
);

export default function Finanzen() {
  const [preset, setPreset] = useState("monat");
  const [von, setVon] = useState(() => presetRange("monat")[0]);
  const [bis, setBis] = useState(() => presetRange("monat")[1]);
  const [ov, setOv] = useState(null);
  const [profits, setProfits] = useState([]);

  useEffect(() => {
    if (!von || !bis) return;
    api.get(`/finance/overview?von=${von}&bis=${bis}`).then((r) => setOv(r.data))
      .catch((e) => { setOv(null); toast.error(apiError(e, "Finanzdaten konnten nicht geladen werden")); });
    api.get(`/finance/article-profits?von=${von}&bis=${bis}`).then((r) => setProfits(r.data))
      .catch(() => setProfits([]));
  }, [von, bis]);

  const pick = (key) => {
    setPreset(key);
    const r = presetRange(key);
    if (r) { setVon(r[0]); setBis(r[1]); }
  };

  const exportCsv = (entity) => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/export/${entity}`, "_blank");
  const guvPdf = () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/finance/guv-pdf?von=${von}&bis=${bis}`, "_blank");

  return (
    <div className="space-y-6" data-testid="finanzen-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-400">Finanzen</div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1">Finanzübersicht</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={guvPdf} data-testid="fin-guv-pdf" className="min-h-[44px]"><FileBarChart className="w-4 h-4 mr-1" /> GuV-PDF / Drucken</Button>
          <Link to="/app/steuerberater">
            <Button className="bg-zinc-900 hover:bg-zinc-800 text-white min-h-[44px]" data-testid="fin-to-steuerberater">Steuerberater-Export <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </Link>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap items-center" data-testid="fin-zeitraum">
        {PRESETS.map(([k, l]) => (
          <button key={k} onClick={() => pick(k)} data-testid={`fin-preset-${k}`}
            className={`text-xs font-mono px-3 py-1.5 rounded-full border min-h-[36px] ${preset === k ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-200 text-zinc-500 hover:border-zinc-400"}`}>{l}</button>
        ))}
        {preset === "frei" && (
          <span className="flex gap-2 items-center">
            <input type="date" value={von} onChange={(e) => setVon(e.target.value)} data-testid="fin-von" className="border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white min-h-[44px]" />
            <span className="text-zinc-400 text-xs">bis</span>
            <input type="date" value={bis} onChange={(e) => setBis(e.target.value)} data-testid="fin-bis" className="border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white min-h-[44px]" />
          </span>
        )}
        <span className="text-xs font-mono text-zinc-400">{deDate(von)} – {deDate(bis)}</span>
      </div>

      {ov && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="fin-kpis">
            <Kpi label="Umsatz" value={eur(ov.umsatz)} sub={`${ov.anzahl.verkaeufe} Verkäufe`} testid="fin-kpi-umsatz" />
            <Kpi label="Kosten" value={eur(ov.kosten_gesamt)} sub="EK + Versand + sonstige" testid="fin-kpi-kosten" />
            <Kpi label="Gewinn" value={eur(ov.gewinn)} sub={`Marge ${String(ov.gewinnmarge).replace(".", ",")} %`} accent testid="fin-kpi-gewinn" />
            <Kpi label="Lagerwert" value={eur(ov.lagerwert)} sub={`${ov.lager_artikel} Artikel`} testid="fin-kpi-lagerwert" />
            <Kpi label="Offene Beträge" value={eur(ov.offene_forderungen)} sub="Forderungen" testid="fin-kpi-offen" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-zinc-200 rounded-xl p-5 shadow-sm" data-testid="fin-ausgaben-card">
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-2">Kosten & Ausgaben</div>
              <div className="divide-y divide-zinc-50">
                <Row label="Einkaufskosten (Wareneinsatz)" value={eur(ov.einkaufskosten)} />
                <Row label="Versandkosten" value={eur(ov.versandkosten)} />
                <Row label="Sonstige Kosten / Gebühren" value={eur(ov.sonstige_kosten)} />
                {Object.entries(ov.kosten_nach_label || {}).map(([l, v]) => <Row key={l} label={`davon: ${l}`} value={eur(v)} />)}
                <Row label="Rückerstattungen (Storno – bereits im Umsatz abgezogen)" value={eur(ov.rueckerstattungen)} />
                <Row label="Wareneinkauf im Zeitraum" value={eur(ov.wareneinkauf)} />
              </div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-xl p-5 shadow-sm" data-testid="fin-steuer-card">
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-2">Steuer & Ergebnis</div>
              <div className="divide-y divide-zinc-50">
                <Row label="Gewinn vor Steuer" value={eur(ov.gewinn_vor_steuer)} />
                <Row label="Umsatzsteuer" value={eur(ov.umsatzsteuer)} />
                <Row label="Vorsteuer (aus Einkäufen)" value={eur(ov.vorsteuer)} />
                <Row label="Steuerlast (USt − Vorsteuer)" value={eur(Math.round((ov.umsatzsteuer - ov.vorsteuer) * 100) / 100)} strong />
                <Row label="Gewinn nach Steuer" value={eur(ov.gewinn)} strong />
              </div>
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
              <div className="text-xs font-mono uppercase tracking-wider text-zinc-400">Gewinn pro Artikel · {profits.length}</div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => exportCsv("verkaeufe")} data-testid="export-sales-button" className="min-h-[40px]"><Download className="w-3.5 h-3.5 mr-1" /> Verkäufe CSV</Button>
                <Button variant="outline" size="sm" onClick={() => exportCsv("ankaeufe")} data-testid="export-purchases-button" className="min-h-[40px]"><Download className="w-3.5 h-3.5 mr-1" /> Ankäufe CSV</Button>
              </div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-xl shadow-sm overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]" data-testid="article-profits-table">
                <thead><tr className="border-b border-zinc-100 text-left">
                  <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Artikel</th>
                  <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Verkauf</th>
                  <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Datum</th>
                  <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400 text-right">EK</th>
                  <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400 text-right">VK</th>
                  <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400 text-right">Kosten</th>
                  <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400 text-right">Steuer</th>
                  <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400 text-right">Gewinn</th>
                </tr></thead>
                <tbody className="divide-y divide-zinc-50">
                  {profits.length === 0 && <tr><td colSpan={8} className="px-5 py-8 text-center text-zinc-400">Keine Verkäufe im Zeitraum.</td></tr>}
                  {profits.map((p, i) => (
                    <tr key={i} className="hover:bg-zinc-50" data-testid={`profit-row-${p.article_number}`}>
                      <td className="px-5 py-3">{p.name} <span className="font-mono text-xs text-zinc-400">{p.article_number}</span></td>
                      <td className="px-5 py-3 font-mono text-xs text-zinc-500">{p.sale_number}</td>
                      <td className="px-5 py-3 text-zinc-600">{deDate(p.date)}</td>
                      <td className="px-5 py-3 text-right text-zinc-500">{eur(p.ek)}</td>
                      <td className="px-5 py-3 text-right font-medium">{eur(p.vk)}</td>
                      <td className="px-5 py-3 text-right text-zinc-500">{eur(p.kosten)}</td>
                      <td className="px-5 py-3 text-right text-zinc-500">{eur(p.steuer)}</td>
                      <td className="px-5 py-3 text-right font-medium text-emerald-600">{eur(p.gewinn)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
